import { computed } from 'mobx'
import { Member } from '@/features/iam/members/Member'
import type {
  PromptDeleteDocumentFn,
  ShowDocumentDetailsFn,
} from '@/features/documents'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type Document } from './Document'
import { type DocumentStore } from './DocumentStore'
import { type UploadBarViewModel } from './UploadBarViewModel'

/**
 * How a document failed processing.
 */
export type DocumentRejectionStatus = 'Quarantined' | 'Failed'

/**
 * View model for a document in a list.
 */
export class DocumentViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    readonly document: Document,
    private readonly _uploadBarVm: UploadBarViewModel,
    private readonly _viewDocumentDetails: ShowDocumentDetailsFn,
    private readonly _promptDelete: PromptDeleteDocumentFn,
    private readonly _documentStore: DocumentStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.view = this.view.bind(this)
    this.download = this.download.bind(this)
    this.delete = this.delete.bind(this)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The member to show as the document's author, falling back to the owner
   * when the author is unknown.
   */
  @computed
  get author() {
    return this.document.author !== Member.none
      ? this.document.author
      : this.document.owner
  }

  /**
   * How much of the document's contents have been uploaded, in percent,
   * or `null` when it isn't being uploaded.
   */
  @computed
  get uploadPercent() {
    return this._uploadBarVm.documentUploadPercent(this.document)
  }

  /**
   * How the document failed processing, if it did.
   */
  @computed
  get rejection(): DocumentRejectionStatus | null {
    const stage = this.document.stage
    if (stage.type !== 'Rejected') {
      return null
    }

    return stage.rejection === 'Quarantined' ? 'Quarantined' : 'Failed'
  }

  /**
   * Shows the document's details.
   */
  view() {
    this._viewDocumentDetails(this.document)
  }

  /**
   * Downloads the document.
   */
  download() {
    return this._documentStore
      .download({ document: this.document, preview: false })
      .catch(this._createAlertErrorHandler('Downloading failed'))
  }

  /**
   * Deletes the document after confirmation.
   */
  async delete() {
    await this._promptDelete({
      document: this.document,
      signal: this._lifecycle.signal,
    })
  }
}
